import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, Lock, Activity, Shield, Gem, UserPlus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import BriefingRequestModal from '../components/BriefingRequestModal';
import { auth } from '../lib/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';

const audits = [
  {
    title: "The Attachment ROI Framework",
    id: "attachment-roi",
    subtitle: "A Neuro-Biological Diagnostic",
    icon: <Activity className="h-6 w-6" />,
    desc: "Quantify the 'Emotional Overhead' of your current attachment pattern and identify biological leaks in your executive function.",
    status: "Available",
    path: "/audit/attachment-roi"
  },
  {
    title: "The Executive Attachment Audit",
    id: "executive-attachment",
    subtitle: "Attachment Infrastructure Evaluation",
    icon: <Shield className="h-6 w-6" />,
    desc: "Identify your core attachment profile and discover how it impacts your leadership, autonomy, and capacity for high-stakes intimacy.",
    status: "Available",
    path: "/audit/executive-attachment"
  },
  {
    title: "The Worthiness Gap Audit",
    id: "worthiness-gap",
    subtitle: "Performance vs. Presence Assessment",
    icon: <Activity className="h-6 w-6" />,
    desc: "Identify whether you are operating from performance or inherent worth in your relationships, and uncover your systemic risk profile.",
    status: "Available",
    path: "/audit/worthiness-gap"
  },
  {
    title: "The Defensive Success Matrix: The Intimidation Delusion Audit",
    id: "intimidation-delusion",
    subtitle: "Defense Mechanism Analysis",
    icon: <Shield className="h-6 w-6" />,
    desc: "Evaluate whether your professional success, intellect, or independence is being unconsciously deployed as an emotional shield.",
    status: "Available",
    path: "/audit/intimidation-delusion"
  },
  {
    title: "The Relational Boundary & Over-Functioning Audit",
    id: "fair-play-cpe",
    subtitle: "A Comprehensive Diagnostic Built on the Fair Play CPE Framework",
    icon: <Activity className="h-6 w-6" />,
    desc: "Calculate your Over-Functioning Ratio by tracking the 4 pillars of cognitive load, adjustment velocity, boundaries, and consequence buffering.",
    status: "Available",
    path: "/audit/fair-play-cpe"
  },
  {
    title: "The Financial Autonomy Index",
    id: "financial-autonomy",
    subtitle: "Structural Capital Review",
    icon: <Gem className="h-6 w-6" />,
    desc: "Identify structural drains on your private capital and assess the true logistical equity of your partnership.",
    status: "Locked",
    path: "#"
  }
];

export default function AuditHub() {
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return unsubscribe;
  }, []);

  const handleAuditClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    if (user && user.emailVerified) {
      navigate(path);
    } else {
      setAuthPromptOpen(true);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 bg-theme-bg text-theme-text">
      <BriefingRequestModal 
        isOpen={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        requestType="Bespoke Executive Diagnostic"
      />
      
      <AnimatePresence>
        {authPromptOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-theme-bg/95 backdrop-blur-md"
              onClick={() => setAuthPromptOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-theme-bg border border-gold/30 p-8 md:p-10 shadow-2xl"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gold"></div>
              
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 flex items-center justify-center border border-gold/40 bg-gold/5 mb-6">
                  <UserPlus className="text-gold h-8 w-8" />
                </div>
                <h2 className="font-serif text-2xl uppercase tracking-widest text-theme-text mb-4">Registration Required</h2>
                <p className="text-taupe text-sm leading-relaxed mb-8 italic">
                  The Executive Audit Hub contains propriety clinical diagnostics. You must register and verify your identity to access these resources.
                </p>
                <div className="flex flex-col w-full space-y-4">
                  <button 
                    onClick={() => navigate('/auth')}
                    className="btn-gold w-full flex justify-center items-center"
                  >
                    Proceed to Registration
                  </button>
                  <button 
                    onClick={() => setAuthPromptOpen(false)}
                    className="px-8 py-3 bg-transparent border border-theme-border text-taupe uppercase tracking-widest text-[10px] font-bold hover:text-theme-text transition-colors"
                  >
                    Return to Library
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8 border-b border-theme-border pb-12">
          <div>
            <span className="kicker">Clinical Diagnostics</span>
            <h1 className="text-4xl md:text-5xl font-serif italic mb-4">The Audit Hub</h1>
            <p className="text-taupe text-sm italic max-w-lg leading-relaxed">
              Proprietary diagnostic matrices designed for high-achieving women. Treat your psychological ecosystem with the same rigor as your financial portfolio.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {audits.map((audit, i) => (
            <motion.div
              key={audit.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`flex flex-col group p-8 border border-solid shadow-md backdrop-blur-sm ${audit.status === 'Available' ? 'border-gold/30 hover:border-gold/100 transition-colors duration-300 hover:shadow-xl bg-black/20 cursor-pointer' : 'border-taupe/30 bg-black/10 cursor-not-allowed opacity-70'} transition-all`}
              onClick={(e) => {
                if (audit.status === 'Available') {
                  handleAuditClick(e as any, audit.path);
                }
              }}
            >
                <div className="flex justify-between items-start mb-8">
                    <div className={`w-12 h-12 flex items-center justify-center border ${audit.status === 'Available' ? 'border-gold text-gold bg-gold/5' : 'border-taupe/20 text-taupe'}`}>
                        {audit.icon}
                    </div>
                    {audit.status === 'Locked' && (
                        <div className="flex flex-col items-end">
                          <div className="flex items-center space-x-1 text-[9px] uppercase tracking-widest text-taupe border border-taupe/20 px-2 py-1 mb-2">
                              <Lock className="h-3 w-3" />
                              <span>Vault Access Required</span>
                          </div>
                          <span className="text-[8px] text-taupe/50 uppercase tracking-tighter italic">Tier 2 Clearance Necessary</span>
                        </div>
                    )}
                </div>
                
                <h3 className={`text-xl font-serif italic mb-2 ${audit.status === 'Locked' ? 'text-theme-text/50' : 'text-theme-text'}`}>{audit.title}</h3>
                <span className={`text-[10px] uppercase tracking-widest block mb-4 ${audit.status === 'Available' ? 'text-gold' : 'text-taupe'}`}>{audit.subtitle}</span>
                <p className={`text-sm leading-relaxed mb-8 italic ${audit.status === 'Locked' ? 'text-taupe/50' : 'text-taupe'}`}>{audit.desc}</p>
                
                <div className="mt-auto border-t border-theme-border pt-6">
                    {audit.status === 'Available' ? (
                         <button onClick={(e) => handleAuditClick(e, audit.path)} className="flex items-center text-[10px] uppercase tracking-widest text-gold hover:text-theme-text font-bold transition-all bg-transparent border-none p-0 cursor-pointer">
                             Commence Audit <ArrowRight className="ml-2 h-3 w-3" />
                         </button>
                    ) : (
                        <div className="flex items-center text-[10px] uppercase tracking-widest text-taupe/50">
                            Unavailable
                        </div>
                    )}
                </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-24 p-12 bg-gold/5 border border-gold/10 text-center">
            <h2 className="text-2xl font-serif italic mb-4">Request a Bespoke Diagnostic</h2>
            <p className="text-sm text-taupe mb-8 italic max-w-lg mx-auto">Require a customized psychological audit or strategic roadmap structurally mapped to your unique high-stakes dynamic?</p>
            <button 
              onClick={() => setRequestModalOpen(true)}
              className="btn-outline"
            >Book Executive Consultation</button>
        </div>
      </div>
    </div>
  );
}
